
import React, { useEffect, useState } from "react";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { UseFormRegister, UseFormWatch, UseFormSetValue } from "react-hook-form";
import { UserProfile } from "@/types/profile";

interface TimeSelectorWithToggleProps {
  day: string;
  register: UseFormRegister<UserProfile>;
  watch: UseFormWatch<UserProfile>;
  setValue: UseFormSetValue<UserProfile>;
}

export const TimeSelectorWithToggle: React.FC<TimeSelectorWithToggleProps> = ({
  day,
  register,
  watch,
  setValue
}) => {
  const [is24Hours, setIs24Hours] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [initialized, setInitialized] = useState(false);
  
  // Default opening and closing times
  const [openingTime, setOpeningTime] = useState<string>("10:00");
  const [closingTime, setClosingTime] = useState<string>("23:00");
  
  // Get current hours from form
  const hoursString = watch(`availability.${day}` as any);
  
  // Parse hours for initial state - only run once when component mounts or when hoursString changes
  useEffect(() => {
    if (!initialized && hoursString) {
      if (hoursString === "24 Hours") {
        setIs24Hours(true);
        setIsClosed(false);
      } else if (hoursString === "Closed") {
        setIsClosed(true);
        setIs24Hours(false);
      } else {
        const timeParts = hoursString.split(" - ");
        if (timeParts && timeParts.length === 2) {
          setOpeningTime(timeParts[0]);
          setClosingTime(timeParts[1]);
        }
        setIs24Hours(false);
        setIsClosed(false);
      }
      setInitialized(true);
    }
  }, [hoursString, initialized]);
  
  // Update hours when inputs change, but only after initialization
  useEffect(() => {
    if (initialized) {
      if (is24Hours) {
        setValue(`availability.${day}` as any, "24 Hours");
      } else if (isClosed) {
        setValue(`availability.${day}` as any, "Closed");
      } else {
        setValue(`availability.${day}` as any, `${openingTime} - ${closingTime}`);
      }
    }
  }, [is24Hours, isClosed, openingTime, closingTime, day, setValue, initialized]);
  
  // Handle 24 Hours toggle
  const handle24HoursChange = (checked: boolean) => {
    setIs24Hours(checked);
    if (checked) {
      setIsClosed(false);
    }
  };
  
  // Handle Closed toggle
  const handleClosedChange = (checked: boolean) => {
    setIsClosed(checked);
    if (checked) {
      setIs24Hours(false);
    }
  };
  
  // Create time options
  const timeOptions = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const formattedHour = hour.toString().padStart(2, "0");
      const formattedMinute = minute.toString().padStart(2, "0");
      timeOptions.push(`${formattedHour}:${formattedMinute}`);
    }
  }
  
  // Add final 23:59 option
  timeOptions.push("23:59");
  
  return (
    <div className="space-y-3">
      {/* Register the field but it will be controlled via state */}
      <input 
        type="hidden" 
        {...register(`availability.${day}` as any)} 
      />
      
      <div className="flex items-center justify-between mb-2 space-x-4">
        <div className="flex items-center space-x-2">
          <Switch 
            id={`${day}-24hours`} 
            checked={is24Hours}
            onCheckedChange={handle24HoursChange}
          />
          <Label htmlFor={`${day}-24hours`}>24 Hours</Label>
        </div>
        
        <div className="flex items-center space-x-2">
          <Switch 
            id={`${day}-closed`} 
            checked={isClosed}
            onCheckedChange={handleClosedChange}
          />
          <Label htmlFor={`${day}-closed`}>Closed</Label>
        </div>
      </div>
      
      {!is24Hours && !isClosed && (
        <div className="flex items-center space-x-2">
          <Select
            disabled={is24Hours || isClosed}
            value={openingTime}
            onValueChange={(value) => setOpeningTime(value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Opening time" />
            </SelectTrigger>
            <SelectContent>
              {timeOptions.map((time) => (
                <SelectItem key={`open-${time}`} value={time}>
                  {time}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <span className="text-center">to</span>
          
          <Select
            disabled={is24Hours || isClosed}
            value={closingTime}
            onValueChange={(value) => setClosingTime(value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Closing time" />
            </SelectTrigger>
            <SelectContent>
              {timeOptions.map((time) => (
                <SelectItem key={`close-${time}`} value={time}>
                  {time}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

export default TimeSelectorWithToggle;
